import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import chevronLeft from "../../assets/chevron-left.svg";
import copyIcon from "../../assets/copy.svg";
import Select from "react-select";
import { OrderSelectStyles, SelectOption, SelectOptionForCustomDelivery } from "../../constants/index.js";
import { getOrderById, updateOrderStatus, updateOrderPaymentStatus, addOrderAttachment, updateOrderAttachment, deleteOrderAttachment } from "../../services/order-service.js";
import { fetchAdjustableSpiceLevels } from "../../services/menu-service.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import Button from "../../components/comman/button.js";
import Toast from "../../components/Toast/toast.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation, getLocalizedValue } from "../../utils/translate.js";
import OrderModal from "../../components/Modal/OrderModal.js";
import ConfirmationModal from "../../components/Modal/ConfirmationModal.jsx";
import { useDarkMode } from "../../utils/darkModeContext.js";
import { getTranslatedPaymentType, getPaymentIconUrl, getPaymentIconClassName } from "../../utils/paymentUtils.js";
import { getOrderStatusStyles } from "../../utils/orderStatusUtils.js";
import { formatPriceWithCurrency } from "../../utils/formatUtils.js";
import { formatDateTimeWithOffset, formatDate, formatTime } from "../../utils/dateUtils.js";
import chatService from "../../services/chat-service.js";
import { DISH_IMAGE_PLACEHOLDER_40 } from "../../utils/dishImagePlaceholder.js";
import { resolveOrderCustomer } from "../../utils/orderCustomer.js";

const OrderContent = () => {
    const {orderId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {language} = useLanguage();
    const {isDarkMode} = useDarkMode();
    const [orderInfo, setOrderInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(null);
    const [isModalVisible, setModalVisible] = useState(false);
    const [spiceLevels, setSpiceLevels] = useState([]);
    const [showDiscount, setShowDiscount] = useState(false);
    const [telegramCustomer, setTelegramCustomer] = useState(null);
    const isFetchingOrder = useRef(false);
    const isFetchingSpiceLevels = useRef(false);
    const toastRef = useRef(null);
    const fileInputRef = useRef(null);
    const [replacingAttachmentId, setReplacingAttachmentId] = useState(null);
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState(null);

    // Memoize translated select options
    const translatedSelectOptions = useMemo(() => {
        if (!orderInfo) return [];
        if (orderInfo.deliveryMethod !== "YANDEX") {
            return SelectOptionForCustomDelivery.map(option => ({
                ...option,
                label: getTranslation(`orderStatuses.${option.value.toLowerCase()}`, language)
            }));
        } else {
            return SelectOption.map(option => ({
                ...option,
                label: getTranslation(`orderStatuses.${option.value.toLowerCase()}`, language)
            }));
        }
    }, [orderInfo, language]);

    const translatedPaymentStatusOptions = useMemo(() => {
        const statuses = ["PAYED", "NOT_PAYED", "REFUNDED", "CANCELLED"];
        return statuses.map(status => ({
            value: status,
            label: getTranslation(`paymentStatuses.${status.toLowerCase()}`, language) || status
        }));
    }, [language]);

    // Use shared order status styles utility
    const getOptionStyles = useCallback(
        (option) => getOrderStatusStyles(option, isDarkMode),
        [isDarkMode]
    );

    // Function to check discount visibility based on order data
    const checkDiscountVisibility = useCallback((orderInfo) => {
        if (!orderInfo?.promoCodeUsages) {
            setShowDiscount(false);
            return;
        }

        // Show discount if there's a price difference or if it's a chef/sponsor-paid discount
        const hasPriceDifference = orderInfo.totalPriceBeforeDiscount && 
            orderInfo.totalPrice && 
            parseFloat(orderInfo.totalPriceBeforeDiscount) > parseFloat(orderInfo.totalPrice);
        
        // For platform-sponsored promocodes, only show discount for cash payments
        // For chef/sponsor-paid, always show discount if there's a price difference
        const sponsor = orderInfo.promoCodeUsages?.sponsor;
        if (sponsor === "PLATFORM") {
            setShowDiscount(hasPriceDifference && orderInfo.paymentType === "CASH");
        } else if (sponsor === "CHEF" || sponsor === "SPLIT") {
            setShowDiscount(hasPriceDifference);
        } else {
            setShowDiscount(hasPriceDifference);
        }
    }, []);

    const apiOrderId = orderInfo?.id ?? orderId;

    useEffect(() => {
        const fetchOrderData = async () => {
            if (!orderId) return;
            
            // Prevent duplicate calls
            if (isFetchingOrder.current) return;
            isFetchingOrder.current = true;

            setLoading(true);
            setTelegramCustomer(null);
            try {
                const order = await getOrderById(orderId);

                // Fetch spice levels (only if not already fetching)
                if (!isFetchingSpiceLevels.current && spiceLevels.length === 0) {
                    isFetchingSpiceLevels.current = true;
                    try {
                        const spiceLevelsData = await fetchAdjustableSpiceLevels();
                        if (spiceLevelsData) {
                            setSpiceLevels(spiceLevelsData);
                        }
                    } catch (error) {
                        console.error("Error fetching spice levels:", error);
                    } finally {
                        isFetchingSpiceLevels.current = false;
                    }
                }

                if (order) {
                    setOrderInfo(order);
                    checkDiscountVisibility(order);
                    // Set the selected option based on the current status
                    const currentStatus = SelectOption.find(option => option.value === order.status);
                    setSelectedOption(currentStatus);
                    
                    if (order.orderPaymentList && order.orderPaymentList.length > 0) {
                        const currentPaymentStatus = translatedPaymentStatusOptions.find(option => option.value === order.orderPaymentList[0].status);
                        setSelectedPaymentStatus(currentPaymentStatus);
                    } else {
                        // Default to NOT_PAYED for cash/card on delivery if no payment record exists yet
                        const defaultStatus = translatedPaymentStatusOptions.find(option => option.value === "NOT_PAYED");
                        setSelectedPaymentStatus(defaultStatus);
                    }
                    
                    if (order.userDto?.id) {
                        try {
                            const customerData = await chatService.getCustomerByUserId(order.userDto.id);
                            if (customerData) {
                                setTelegramCustomer(customerData);
                            }
                        } catch (err) {
                            // Silently ignore if not found
                        }
                    }
                } else {
                    // Order not found
                    navigate("/orders");
                    toastRef.current?.addToast(getTranslation('orderContentPage.orderNotFound', language), "error");
                }
            } catch (error) {
                console.error("Error fetching order data:", error);
                toastRef.current?.addToast(getTranslation('common.error', language), "error");
                navigate("/orders");
            } finally {
                setLoading(false);
                isFetchingOrder.current = false;
            }
        };
        fetchOrderData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId, navigate, language, checkDiscountVisibility]);

    const handleStatusChange = async (option) => {
        setSelectedOption(option);

        if (option.value === "DECLINED") {
            setModalVisible(true);
            return;
        }

        try {
            await updateOrderStatus(apiOrderId, option.value);
            // Update the local state
            setOrderInfo(prev => ({
                ...prev,
                status: option.value
            }));
            toastRef.current?.addToast(getTranslation('common.success', language), "success");
        } catch (error) {
            console.error("Failed to update order status:", error);
            toastRef.current?.addToast(getTranslation('common.error', language), "error");
        }
    };

    const handlePaymentStatusChange = async (option) => {
        setSelectedPaymentStatus(option);
        if (!orderInfo) return;

        try {
            await updateOrderPaymentStatus(apiOrderId, option.value);
            setOrderInfo(prev => {
                const updatedPayments = [...(prev.orderPaymentList || [])];
                if (updatedPayments.length > 0) {
                    updatedPayments[0] = { ...updatedPayments[0], status: option.value };
                } else {
                    // If no payment list existed, we can just let the next fetch handle it
                    // or create a dummy one if we want optimistic UI
                }
                return { ...prev, orderPaymentList: updatedPayments };
            });
            toastRef.current?.addToast(getTranslation('common.success', language), "success");
        } catch (error) {
            console.error("Failed to update payment status:", error);
            // Revert on error if needed, but the select already changed
            toastRef.current?.addToast(getTranslation('common.error', language), "error");
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const response = await addOrderAttachment(apiOrderId, file);
            // The response for addOrderAttachment is the full OrderDto according to the backend controller
            if (response && response.attachments) {
                setOrderInfo(prev => ({
                    ...prev,
                    ...response,
                    attachments: response.attachments
                }));
                toastRef.current?.addToast(getTranslation('common.success', language), "success");
            }
        } catch (error) {
            console.error("Failed to upload receipt:", error);
            toastRef.current?.addToast(getTranslation('common.error', language), "error");
        }
        // Reset input
        e.target.value = '';
    };

    const handleFileReplaceTrigger = (attachmentId) => {
        setReplacingAttachmentId(attachmentId);
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (replacingAttachmentId) {
            await handleFileReplace(replacingAttachmentId, file);
            setReplacingAttachmentId(null);
        } else {
            await handleFileUpload(e);
        }
        e.target.value = '';
    };

    const handleFileReplace = async (attachmentId, file) => {
        if (!file) return;

        try {
            const response = await updateOrderAttachment(apiOrderId, attachmentId, file);
            // The response for updateOrderAttachment is also the full OrderDto
            if (response && response.attachments) {
                setOrderInfo(prev => ({
                    ...prev,
                    ...response,
                    attachments: response.attachments
                }));
                toastRef.current?.addToast(getTranslation('common.success', language), "success");
            }
        } catch (error) {
            console.error("Failed to replace receipt:", error);
            toastRef.current?.addToast(getTranslation('common.error', language), "error");
        }
    };

    const handleFileDelete = (attachmentId) => {
        setAttachmentToDelete(attachmentId);
        setDeleteModalVisible(true);
    };

    const confirmDelete = async () => {
        if (!attachmentToDelete) return;
        
        try {
            await deleteOrderAttachment(apiOrderId, attachmentToDelete);
            setOrderInfo(prev => ({
                ...prev,
                attachments: (prev.attachments || []).filter(a => String(a.id) !== String(attachmentToDelete))
            }));
            toastRef.current?.addToast(getTranslation('common.success', language), "success");
        } catch (error) {
            console.error("Failed to delete receipt:", error);
            toastRef.current?.addToast(getTranslation('common.error', language), "error");
        } finally {
            setDeleteModalVisible(false);
            setAttachmentToDelete(null);
        }
    };

    const handleCopyOrderId = () => {
        navigator.clipboard.writeText(orderInfo.number);
        toastRef.current?.addToast(getTranslation('common.success', language), "success");
    };

    const onBack = () => {
        // Check if user came from Clients page
        if (location.state?.fromPage === 'clients') {
            navigate('/clients');
            return;
        }
        
        if (location.state?.fromPage === "board") {
            navigate("/orders/board");
            return;
        }
        const fromTab = location.state?.fromTab || "ongoing";
        const ongoingSubTab = location.state?.ongoingSubTab;
        if (fromTab === "dishes") {
            navigate("/orders", { state: { activeTab: "ongoing", ongoingSubTab: "dishes" } });
            return;
        }
        if (fromTab === "history") {
            navigate("/orders", { state: { activeTab: "history" } });
            return;
        }
        navigate("/orders", {
            state: {
                activeTab: "ongoing",
                ongoingSubTab: ongoingSubTab === "dishes" ? "dishes" : "orders"
            }
        });
    };

    // Payment utilities are imported from paymentUtils

    const handleModalClose = () => {
        setModalVisible(false);
        // Refresh order data to get the updated status
        const fetchOrderData = async () => {
            if (!orderId) return;

            try {
                const order = await getOrderById(orderId);

                if (order) {
                    setOrderInfo(order);
                    // Update the selected option to match the current status
                    const currentStatus = SelectOption.find(option => option.value === order.status);
                    setSelectedOption(currentStatus);
                    if (order.orderPaymentList && order.orderPaymentList.length > 0) {
                        const currentPaymentStatus = translatedPaymentStatusOptions.find(option => option.value === order.orderPaymentList[0].status);
                        setSelectedPaymentStatus(currentPaymentStatus);
                    } else {
                        const defaultStatus = translatedPaymentStatusOptions.find(option => option.value === "NOT_PAYED");
                        setSelectedPaymentStatus(defaultStatus);
                    }
                    // Refresh discount visibility
                    checkDiscountVisibility(order);
                }
            } catch (error) {
                console.error("Error fetching order data:", error);
                toastRef.current?.addToast(getTranslation('common.error', language), "error");
            }
        };

        fetchOrderData();
    };

    // Extract order data with safe defaults
    const {
        number,
        receiverPhoneNumber = '',
        deliveryDateTime = '',
        addressDto = {},
        deliveryPrice = '',
        note = '',
        totalPrice = '',
        totalPriceBeforeDiscount = '',
        orderDishList = [],
        paymentType = '',
        createdAt = '',
        asSoonAsPossible = false,
        promoCodeUsages = null,
        promoCode = null,
        attachments = [],
        companyName = null,
        userDto = null,
        externalUser = null,
    } = orderInfo || {};

    const customer = resolveOrderCustomer({
        userDto,
        externalUser,
        receiverPhoneNumber,
        companyName,
    });
    const customerFullName = customer.fullName;
    const customerPhone = customer.phone;
    const displayCompanyName = customer.companyName;

    const isTelegramOrder = number?.endsWith('_TG');

    // Memoized formatted prices (must be before early returns per Rules of Hooks)
    const formattedDeliveryPrice = useMemo(
        () => formatPriceWithCurrency(deliveryPrice),
        [deliveryPrice]
    );

    const formattedTotalPrice = useMemo(
        () => formatPriceWithCurrency(totalPriceBeforeDiscount || totalPrice || 0),
        [totalPriceBeforeDiscount, totalPrice]
    );

    const formattedTotalWithDiscount = useMemo(
        () => formatPriceWithCurrency(totalPrice || 0),
        [totalPrice]
    );

    // Calculate discount amount
    const discountAmount = useMemo(() => {
        if (!totalPriceBeforeDiscount || !totalPrice) return null;
        const before = parseFloat(totalPriceBeforeDiscount);
        const after = parseFloat(totalPrice);
        if (isNaN(before) || isNaN(after) || before <= after) return null;
        return before - after;
    }, [totalPriceBeforeDiscount, totalPrice]);

    // Determine if discount should be shown (based on actual discount or showDiscount flag)
    const shouldShowDiscount = useMemo(() => {
        return discountAmount !== null || (showDiscount && totalPriceBeforeDiscount && totalPrice);
    }, [discountAmount, showDiscount, totalPriceBeforeDiscount, totalPrice]);

    // Early returns after all hooks
    if (loading) {
        return <LoadingSpinner/>;
    }

    if (!orderInfo) {
        return <div className="p-4">{getTranslation('orderContentPage.orderNotFound', language)}</div>;
    }

    const isTakeaway = orderInfo?.deliveryMethod === 'TAKEAWAY';

    const addressQuery = encodeURIComponent(`${addressDto?.country || ''} ${addressDto?.city || ''} ${addressDto?.street || ''} ${addressDto?.home || ''}`);
    const yandexMapUrl = `https://yandex.com/maps/?text=${addressQuery}`;

    return (
        <div className="max-w-7xl mx-auto bg-white dark:bg-[#161b22] p-4 border dark:border-[#30363d] rounded-lg">
            <Button
                variant="secondary"
                size="md"
                onClick={onBack}
                className="mb-6 font-bold"
            >
                <img
                    className="w-4 h-4 mr-2 dark:invert dark:opacity-80"
                    src={chevronLeft || ""}
                    alt={getTranslation('pagination.previous', language)}
                />
                {getTranslation('orderContentPage.backToList', language)}
            </Button>

            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                <div className="flex items-center mb-4 md:mb-0">
                    <h1 className="text-gray-900 dark:text-[#c9d1d9] text-xl font-bold">{getTranslation('orderContentPage.order', language)}</h1>
                    <p className="font-bold text-sm text-gray-900 dark:text-[#c9d1d9] ml-3">#{number || orderId}</p>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyOrderId}
                        title={getTranslation('orderContentPage.copyOrderId', language)}
                        className="ml-2 p-1"
                    >
                        <img
                            className="w-4 h-4 cursor-pointer dark:invert dark:opacity-80"
                            src={copyIcon || ""}
                            alt={getTranslation('common.copy', language)}
                        />
                    </Button>
                </div>
                <div className="md:w-auto flex flex-wrap items-center justify-end gap-2">
                    {["DELIVERED", "DECLINED", "INITIAL"].includes(orderInfo?.status) ? (
                        <div 
                            className="px-4 py-2 rounded-md font-bold text-sm whitespace-nowrap w-[221px] text-center"
                            style={{
                                backgroundColor: getOptionStyles({value: orderInfo?.status}).backgroundColor,
                                color: getOptionStyles({value: orderInfo?.status}).color
                            }}
                        >
                            {getTranslation(`orderStatuses.${orderInfo?.status?.toLowerCase()}`, language)}
                        </div>
                    ) : (
                        <Select
                            className="w-[221px] whitespace-nowrap"
                            options={translatedSelectOptions}
                            placeholder={getTranslation(`orderStatuses.${orderInfo?.status?.toLowerCase()}`, language)}
                            styles={{
                                ...OrderSelectStyles,
                                control: (provided, state) => ({
                                    ...provided,
                                    paddingRight: "8px", // Consistent padding to move arrow away from right edge
                                    backgroundColor: getOptionStyles(selectedOption || {value: orderInfo?.status}).backgroundColor,
                                    color: getOptionStyles(selectedOption || {value: orderInfo?.status}).color,
                                    borderColor: getOptionStyles(selectedOption || {value: orderInfo?.status}).color,
                                    boxShadow: state.isFocused ? `0 0 0 1px ${getOptionStyles(selectedOption || {value: orderInfo?.status}).color}` : null,
                                    '&:hover': {
                                        borderColor: getOptionStyles(selectedOption || {value: orderInfo?.status}).color
                                    },
                                    fontWeight: "700",
                                    fontSize: "14px"
                                }),
                                singleValue: (provided, state) => ({
                                    ...provided,
                                    color: getOptionStyles(selectedOption || {value: orderInfo?.status}).color
                                }),

                                option: (provided, {data, isSelected, isFocused}) => ({
                                    ...provided,
                                    backgroundColor: isSelected 
                                        ? getOptionStyles(data).backgroundColor 
                                        : isFocused 
                                            ? (isDarkMode ? "#161b22" : "#f9fafb") 
                                            : (isDarkMode ? "#0d1117" : "white"),
                                    color: getOptionStyles(data).color,
                                    fontWeight: "700",
                                    fontSize: "14px",
                                    ':active': {
                                        backgroundColor: getOptionStyles(data).backgroundColor
                                    }
                                }),
                                menu: (provided) => ({
                                    ...provided,
                                    backgroundColor: isDarkMode ? "#0d1117" : "white",
                                    border: `1px solid ${isDarkMode ? "#30363d" : "#e5e7eb"}`
                                }),
                                dropdownIndicator: (provided) => ({
                                    ...provided,
                                    color: getOptionStyles(selectedOption || {value: orderInfo?.status}).color,
                                    '&:hover': {
                                        color: getOptionStyles(selectedOption || {value: orderInfo?.status}).color
                                    }
                                })
                            }}
                            value={translatedSelectOptions.find(option => option.value === (selectedOption?.value || orderInfo?.status)) || null}
                            onChange={handleStatusChange}
                        />
                    )}
                </div>
            </div>

            

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-7 pr-0 lg:pr-6">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 gap-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <p className="text-gray-900 dark:text-[#c9d1d9] font-bold text-base">
                                {getTranslation('orderContentPage.transactionDate', language)}: {formatDateTimeWithOffset(createdAt || deliveryDateTime)}
                            </p>
                            <div className="flex items-center">
                                {paymentType === "TELCELL" ? (
                                    <svg 
                                        className={getPaymentIconClassName(paymentType)} 
                                        fill="none" 
                                        viewBox="0 0 162 30" 
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M16.3593 1.05288V28.9605H9.18531V8.17342H0.733398V1.05288H16.3593ZM18.6001 8.17954H25.7741V1.05899H18.6001V8.17954ZM47.4804 28.9605V21.8461H35.2543V18.5701H44.6096V11.4495H35.2543V8.17342H47.4804V1.05288H28.0802V28.9605H47.4804ZM69.3948 28.9605V21.8461H58.0543V1.05288H50.8802V28.9605H69.3948ZM83.4516 8.14897C86.2273 8.14897 88.1411 9.85424 88.8544 11.8406L96.4862 11.8468C95.6774 5.61247 90.2274 0.600586 83.3446 0.600586C75.2077 0.600586 69.5434 6.62707 69.5434 15.0006C69.5434 23.3741 75.3979 29.4006 83.3446 29.4006C90.061 29.4006 95.446 25.006 96.5748 18.1789L88.8247 18.1727C88.1352 20.3608 86.0074 21.8522 83.4456 21.8522C79.6476 21.8522 77.0681 19.0285 77.0681 15.0006C77.0741 10.9727 79.5585 8.14897 83.4516 8.14897ZM98.6374 1.05288V28.9605H118.038V21.8461H105.811V18.5701H115.167V11.4495H105.811V8.17342H118.038V1.05288H98.6374ZM139.941 28.9605V21.8461H128.605V1.05288H121.431V28.9605H139.941ZM149.854 1.05288H142.681V28.9605H161.189V21.8461H149.854V1.05288Z" className="fill-gray-900 dark:fill-white" />
                                    </svg>
                                ) : (paymentType === "CASH" || paymentType === "B2B") ? (
                                    <svg
                                        className={getPaymentIconClassName(paymentType)}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                                            className="stroke-gray-600 dark:stroke-gray-400"
                                        />
                                    </svg>
                                ) : getPaymentIconUrl(paymentType) ? (
                                    <img
                                        className={getPaymentIconClassName(paymentType)}
                                        src={getPaymentIconUrl(paymentType)}
                                        alt={paymentType || ""}
                                    />
                                ) : null}
                                <span className={`${paymentType !== "CASH" && paymentType !== "B2B" && getPaymentIconUrl(paymentType) ? "ml-2" : ""} text-sm text-gray-700 dark:text-[#c9d1d9] font-medium`}>
                                    {getTranslatedPaymentType(paymentType, language)}
                                </span>
                            </div>
                        </div>
                        
                        {(paymentType === 'CASH' || paymentType === 'CARD_ON_DELIVERY') ? (
                            <div className="flex items-center gap-2 w-full lg:w-auto mt-2 lg:mt-0">
                                <span className="text-sm font-medium text-gray-700 dark:text-[#c9d1d9] whitespace-nowrap">
                                    {getTranslation('orderContentPage.paymentStatus', language)}:
                                </span>
                                <Select
                                    isSearchable={false}
                                    className="w-[160px] whitespace-nowrap"
                                    options={translatedPaymentStatusOptions}
                                    placeholder={orderInfo.orderPaymentList?.[0] ? (getTranslation(`paymentStatuses.${orderInfo.orderPaymentList[0].status?.toLowerCase()}`, language) || orderInfo.orderPaymentList[0].status) : getTranslation('paymentStatuses.not_payed', language)}
                                    styles={{
                                        control: (provided, state) => ({
                                            ...provided,
                                            paddingRight: "8px",
                                            backgroundColor: isDarkMode ? "#0d1117" : "white",
                                            minHeight: "36px",
                                            height: "36px",
                                            borderColor: isDarkMode ? "#30363d" : "#e5e7eb",
                                            boxShadow: state.isFocused ? "0 0 0 1px rgba(0,0,0,0.1)" : null,
                                            fontWeight: "600",
                                            fontSize: "13px",
                                            color: isDarkMode ? "#c9d1d9" : "#374151"
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: isDarkMode ? "#c9d1d9" : "#374151"
                                        }),
                                        option: (provided, {isSelected, isFocused}) => ({
                                            ...provided,
                                            backgroundColor: isSelected ? (isDarkMode ? "#21262d" : "#f3f4f6") : isFocused ? (isDarkMode ? "#161b22" : "#f9fafb") : (isDarkMode ? "#0d1117" : "white"),
                                            color: isDarkMode ? "#c9d1d9" : "#374151",
                                            fontWeight: "600",
                                            fontSize: "13px"
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            backgroundColor: isDarkMode ? "#0d1117" : "white",
                                            border: `1px solid ${isDarkMode ? "#30363d" : "#e5e7eb"}`
                                        })
                                    }}
                                    value={translatedPaymentStatusOptions.find(option => option.value === (selectedPaymentStatus?.value || orderInfo.orderPaymentList[0].status)) || null}
                                    onChange={handlePaymentStatusChange}
                                />
                            </div>
                        ) : orderInfo.orderPaymentList?.[0] ? (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold mt-2 lg:mt-0 ${
                                orderInfo.orderPaymentList[0].status === 'PAYED'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-800/50'
                                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50'
                            }`}>
                                {getTranslation(`paymentStatuses.${orderInfo.orderPaymentList[0].status?.toLowerCase()}`, language) || orderInfo.orderPaymentList[0].status}
                            </span>
                        ) : null}
                    </div>

                    <div className="bg-white dark:bg-[#161b22] rounded mb-5 border dark:border-[#30363d] p-4">
                        <h3 className="font-bold mb-4 text-lg text-gray-900 dark:text-[#c9d1d9]">{getTranslation('orderContentPage.orderedDishes', language)}</h3>
                        {orderDishList?.map((dish, index) => (
                            <div key={index} className="mb-4 pb-4 border-b dark:border-[#30363d] last:border-b-0">
                                <div className="flex items-center mb-2">
                                    <img
                                        src={dish?.dishDto?.url || DISH_IMAGE_PLACEHOLDER_40}
                                        alt={getLocalizedValue(dish?.dishDto, 'name', language) || getTranslation('common.dish', language)}
                                        className="w-12 h-12 rounded-lg border dark:border-[#30363d] object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = DISH_IMAGE_PLACEHOLDER_40;
                                        }}
                                    />
                                    <div className="ml-3 flex-grow">
                                        <p className="font-medium text-gray-900 dark:text-[#c9d1d9]">{getLocalizedValue(dish?.dishDto, 'name', language)}</p>
                                        <div className="flex justify-between items-center">
                                            <span
                                                className="font-bold text-m text-gray-700 dark:text-[#b1bac4]"> {dish?.quantity} հատ</span>
                                            {dish?.price ? (
                                                    <span className="text-gray-700 dark:text-[#b1bac4] font-medium">{formatPriceWithCurrency(dish?.price)}</span>) :
                                                <span/>}
                                        </div>
                                        {dish?.selectedSpiceLevel && (
                                            <div className="text-sm text-gray-600 dark:text-[#8b949e] mt-1">
                                                <span className="flex justify-between w-full">
                                                    <span
                                                        className="font-medium">{getTranslation('common.spiceLevel', language) || "Spice Level"}</span>{" "}
                                                    {spiceLevels.find(s => s.id === dish.selectedSpiceLevel).spiceLevel}
                                                </span>
                                            </div>
                                        )}
                                        {dish?.orderDishAdditionDtoList?.length > 0 && (
                                            dish.orderDishAdditionDtoList.map((item, idx) => (
                                                <div key={idx} className="text-sm text-gray-600 dark:text-[#8b949e]">
                                                    <span className="flex justify-between w-full">
                                                    <span
                                                        className="font-medium">{getLocalizedValue(item.dishAdditionDto.group, 'name', language)}</span>
                                                        {
                                                            item.price ?
                                                                (
                                                                    <span className="flex justify-between w-full">
                                                                        <span
                                                                            className="ml-2 flex">{getLocalizedValue(item.dishAdditionDto, 'name', language)}</span>
                                                                        <span
                                                                            className="text-m text-gray-700 dark:text-[#b1bac4] font-medium">{formatPriceWithCurrency(item.price)}</span>
                                                                    </span>
                                                                )
                                                                :
                                                                (
                                                                    <span>{getLocalizedValue(item.dishAdditionDto, 'name', language)}</span>)
                                                        }
                                                    </span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {
                        note && (
                            <div
                                className={`${isTakeaway
                                    ? 'bg-white dark:bg-gray-800/40 border-gray-300 dark:border-gray-700 shadow-sm'
                                    : 'bg-white dark:bg-gray-900/30 border-gray-200 dark:border-gray-700'} border-2 rounded-lg p-5 transition-all`}
                                role="status"
                                aria-live="polite"
                            >
                                <div className="flex items-center">
                                    <div className="flex-1">
                                        <span
                                            className={`text-xs font-medium uppercase tracking-wide ${isTakeaway ? 'text-gray-600 dark:text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                            {getTranslation('orderContentPage.notes', language)}
                                    </span>
                                        <div className="flex items-center mt-1">
                                            <span
                                                className={`text-base font-bold text-gray-900 dark:text-gray-300`}>{note}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    <div className="text-sm space-y-4 border-t dark:border-[#30363d] pt-4">
                        <div className="flex justify-between">
                            <span
                                className="font-medium text-gray-900 dark:text-[#c9d1d9]">{getTranslation('orderContentPage.deliveryAmount', language)}</span>
                            <span className="text-gray-600 dark:text-[#b1bac4] font-medium">{formattedDeliveryPrice}</span>
                        </div>
                        {(promoCode || promoCodeUsages?.promoCode || promoCodeUsages?.code) && (
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">{getTranslation('orderContentPage.promocode', language)}</span>
                                <span className="text-gray-600 dark:text-[#b1bac4] font-medium">{promoCode || promoCodeUsages?.promoCode || promoCodeUsages?.code}</span>
                            </div>
                        )}
                        {discountAmount !== null && (
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">{getTranslation('orderContentPage.discountAmount', language)}</span>
                                <span className="text-gray-600 dark:text-[#b1bac4] font-medium">-{formatPriceWithCurrency(discountAmount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between font-semibold text-gray-900 dark:text-[#c9d1d9] mt-4 text-lg">
                            <span>{getTranslation('orderContentPage.total', language)}:</span>
                            <span>{formattedTotalPrice}</span>
                        </div>
                        {shouldShowDiscount && (
                            <div className="flex justify-between font-semibold text-gray-900 dark:text-[#c9d1d9] mt-4 text-lg">
                                <span>{getTranslation('orderContentPage.totalWithDiscount', language)}:</span>
                                <span>{formattedTotalWithDiscount}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="lg:col-span-5">
                    <div className="bg-white dark:bg-[#0d1117] p-6 rounded-lg shadow-sm border border-gray-100 dark:border-[#30363d]">
                        <h3 className="text-gray-900 dark:text-[#c9d1d9] text-lg font-bold mb-6 border-b border-gray-200 dark:border-[#30363d] pb-3">
                            {getTranslation('orderContentPage.additionalInfo', language)}
                        </h3>
                        <div className="space-y-6 text-sm">
                            <div
                                className={`${isTakeaway 
                                    ? 'bg-white dark:bg-gray-800/40 border-gray-300 dark:border-gray-700 shadow-sm' 
                                    : 'bg-white dark:bg-gray-900/30 border-gray-200 dark:border-gray-700'} border-2 rounded-lg p-5 transition-all`}
                                role="status"
                                aria-live="polite"
                            >
                                <div className="flex items-center">
                                    {isTakeaway ? (
                                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 mr-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-gray-700 dark:text-gray-300">
                                            <path d="M8 7a4 4 0 1 1 8 0v1h1.5A1.5 1.5 0 0 1 19 9.5v9A2.5 2.5 0 0 1 16.5 21h-9A2.5 2.5 0 0 1 5 18.5v-9A1.5 1.5 0 0 1 6.5 8H8V7Zm2 1h4V7a2 2 0 1 0-4 0v1Z"/>
                                        </svg>
                                        </div>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5 mr-2 text-gray-700 dark:text-gray-400" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h12v7H3zM15 10h3l3 3v1h-6V10zM6 20a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm10 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" />
                                        </svg>
                                    )}
                                    <div className="flex-1">
                                        <span className={`text-xs font-medium uppercase tracking-wide ${isTakeaway ? 'text-gray-600 dark:text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                            {getTranslation('orderContentPage.deliveryWay', language)}
                                    </span>
                                        <div className="flex items-center mt-1">
                                            <span className={`text-base font-bold ${isTakeaway ? 'text-gray-800 dark:text-gray-200' : 'text-gray-900 dark:text-gray-300'}`}>
                                        {isTakeaway ? getTranslation('orderContentPage.takeaway', language) : getTranslation('orderContentPage.delivery', language)}
                                    </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Receiving method now shown as a badge in header */}


                            <div className="p-4 bg-white dark:bg-[#161b22] rounded-md shadow-sm border-l-4 border-customOrange/60 dark:border-orange-500/40">
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-[#8b949e] mb-2">
                                    {getTranslation('orderContentPage.customer', language)}
                                </p>
                                <div className="min-w-0 flex flex-col gap-1.5">
                                    {customerFullName ? (
                                        <>
                                            <p className="text-sm font-semibold text-gray-900 dark:text-[#c9d1d9] leading-snug break-words">
                                                {customerFullName}
                                            </p>
                                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:flex-wrap sm:gap-x-2">
                                                <span className="text-[11px] font-medium text-gray-500 dark:text-[#8b949e] shrink-0">
                                                    {getTranslation('orderContentPage.phoneNumber', language)}
                                                </span>
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    {customerPhone ? (
                                                        customer.isExternalSnapshot ? (
                                                            <span className="text-sm font-medium text-gray-900 dark:text-[#c9d1d9]">
                                                                {customerPhone}
                                                            </span>
                                                        ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate('/clients', { state: { searchPhone: customerPhone } })}
                                                            className="text-left text-sm font-medium text-customOrange dark:text-customOrange hover:underline transition-colors truncate max-w-full"
                                                        >
                                                            {customerPhone}
                                                        </button>
                                                        )
                                                    ) : (
                                                        <span className="text-sm text-gray-500 dark:text-[#8b949e]">—</span>
                                                    )}
                                                    {telegramCustomer && (
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`/chat`, { state: { selectedCustomerId: telegramCustomer.id } })}
                                                            className="inline-flex shrink-0 items-center justify-center rounded-md p-1 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                                                            title={getTranslation('orderContentPage.chatWithCustomer', language) || "Chat with customer"}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.436 3 12c0 2.206 1.129 4.195 2.923 5.378.134.088.225.248.18.409l-.499 1.77a.75.75 0 00.916.916l1.77-.499a.466.466 0 01.409.18C9.805 21.121 10.894 21.25 12 21.25z" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {customerPhone ? (
                                                customer.isExternalSnapshot ? (
                                                    <span className="text-sm font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                                        {customerPhone}
                                                    </span>
                                                ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => navigate('/clients', { state: { searchPhone: customerPhone } })}
                                                    className="text-left text-sm font-semibold text-customOrange dark:text-customOrange hover:underline transition-colors truncate max-w-full"
                                                >
                                                    {customerPhone}
                                                </button>
                                                )
                                            ) : (
                                                <span className="text-gray-500 dark:text-[#8b949e] text-sm">—</span>
                                            )}
                                            {telegramCustomer && (
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/chat`, { state: { selectedCustomerId: telegramCustomer.id } })}
                                                    className="inline-flex shrink-0 items-center justify-center rounded-md p-1 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                                                    title={getTranslation('orderContentPage.chatWithCustomer', language) || "Chat with customer"}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.436 3 12c0 2.206 1.129 4.195 2.923 5.378.134.088.225.248.18.409l-.499 1.77a.75.75 0 00.916.916l1.77-.499a.466.466 0 01.409.18C9.805 21.121 10.894 21.25 12 21.25z" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {displayCompanyName && (
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-4 bg-white dark:bg-[#161b22] rounded-md shadow-sm border-l-4 border-gray-300 dark:border-[#30363d] gap-2">
                                    <span className="font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                        {getTranslation('orderPage.company', language) || 'Company'}
                                    </span>
                                    <span className="text-gray-700 dark:text-[#b1bac4] font-medium text-left sm:text-right break-words">
                                        {displayCompanyName}
                                    </span>
                                </div>
                            )}

                            <div className="flex flex-col p-4 bg-white dark:bg-[#161b22] rounded-md shadow-sm border-l-4 border-gray-300 dark:border-[#30363d] gap-3">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                                    <span className="font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                        {getTranslation('orderContentPage.scheduledDate', language)}
                                    </span>
                                    <div className="text-gray-700 dark:text-[#b1bac4] font-medium text-left sm:text-right">
                                        {formatDate(deliveryDateTime)}
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                                    <span className="font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                        {getTranslation('orderContentPage.scheduledTime', language)}
                                    </span>
                                    <div className="flex flex-wrap items-center gap-2 sm:justify-end text-left sm:text-right">
                                        <span className="text-gray-700 dark:text-[#b1bac4] font-medium">{formatTime(deliveryDateTime)}</span>
                                        {asSoonAsPossible && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-white dark:bg-gray-800/50 text-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                </svg>
                                                {getTranslation('orderContentPage.asSoonAsPossible', language)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {!isTakeaway && (<div className="p-4 bg-white dark:bg-[#161b22] rounded-md shadow-sm border-l-4 border-gray-300 dark:border-[#30363d]">
                                <div className="mb-3">
                                    <span className="font-semibold text-gray-900 dark:text-[#c9d1d9]">{getTranslation('orderContentPage.address', language)}</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                                    {addressDto?.country && (
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">{getTranslation('orderContentPage.country', language)}</span>
                                            <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">{addressDto.country}</span>
                                        </div>
                                    )}
                                    {addressDto?.city && (
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">{getTranslation('orderContentPage.city', language)}</span>
                                            <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">{addressDto.city}</span>
                                        </div>
                                    )}
                                    {addressDto?.street && (
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">{getTranslation('orderContentPage.street', language)}</span>
                                            <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">{addressDto.street}</span>
                                        </div>
                                    )}
                                    {addressDto?.home && (
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">{getTranslation('orderContentPage.home', language)}</span>
                                            <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">{addressDto.home}</span>
                                        </div>
                                    )}
                                    {addressDto?.floor && (
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">{getTranslation('orderContentPage.floor', language)}</span>
                                            <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">{addressDto.floor}</span>
                                        </div>
                                    )}
                                    {addressDto?.entrance && (
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">{getTranslation('orderContentPage.entrance', language)}</span>
                                            <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">{addressDto.entrance}</span>
                                        </div>
                                    )}
                                    {addressDto?.flat && (
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">{getTranslation('orderContentPage.flat', language)}</span>
                                            <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">{addressDto.flat}</span>
                                        </div>
                                    )}
                                </div>
                                <a
                                    href={yandexMapUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 transition-colors mt-2 font-medium"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                    {getTranslation('orderContentPage.viewOnMap', language)}
                                </a>
                            </div>)}

                            {isTelegramOrder && (
                                <div className="mt-6 bg-white dark:bg-[#0d1117] p-6 rounded-lg shadow-sm border border-gray-100 dark:border-[#30363d]">
                                    <div className="flex items-center justify-between mb-4 border-b border-gray-200 dark:border-[#30363d] pb-3">
                                        <h3 className="text-gray-900 dark:text-[#c9d1d9] text-lg font-bold">
                                            {getTranslation('orderContentPage.receipts', language)}
                                        </h3>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setReplacingAttachmentId(null);
                                                fileInputRef.current?.click();
                                            }}
                                            className="text-customOrange font-bold"
                                        >
                                            {getTranslation('orderContentPage.addReceipt', language)}
                                        </Button>
                                    </div>
                                    
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        className="hidden"
                                        accept="image/*,application/pdf"
                                    />

                                    <div className="space-y-4">
                                        {attachments?.length > 0 ? (
                                            attachments.map((attachment) => (
                                                <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#161b22] rounded-md border dark:border-[#30363d]">
                                                    <a 
                                                        href={attachment.url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="text-sm font-medium text-customOrange hover:underline truncate mr-4"
                                                    >
                                                        {attachment.url.split('/').pop() || `Receipt-${String(attachment.id).substring(0, 8)}`}
                                                    </a>
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleFileReplaceTrigger(attachment.id)}
                                                            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 p-1"
                                                            title={getTranslation('orderContentPage.replace', language)}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                                            </svg>
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleFileDelete(attachment.id)}
                                                            className="text-red-500 hover:text-red-700 p-1"
                                                            title={getTranslation('common.delete', language)}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                                            </svg>
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-500 dark:text-gray-400 italic text-center py-2">
                                                {getTranslation('orderContentPage.noReceipts', language)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {isModalVisible && <OrderModal closeModal={handleModalClose} orderId={apiOrderId} />}
                    {isDeleteModalVisible && (
                        <ConfirmationModal
                            description={getTranslation('common.confirmDelete', language)}
                            onCancel={() => setDeleteModalVisible(false)}
                            onSubmit={confirmDelete}
                        />
                    )}
                </div>
            </div>
            <Toast ref={toastRef} />
        </div>
    );
};

export default OrderContent;
